import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import PixelCard from '../blocks/Components/PixelCard/PixelCard';
import SpotlightCard from '../blocks/Components/SpotlightCard/SpotlightCard';
import Particles from '../blocks/Backgrounds/Particles/Particles';

interface BaseCardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'sm' | 'md' | 'lg';
}

interface PixelCardWrapperProps extends BaseCardProps {
  variant?: 'pixel';
  interactive?: boolean;
}

interface SpotlightCardWrapperProps extends BaseCardProps {
  variant?: 'spotlight';
  spotlightColor?: `rgba(${number}, ${number}, ${number}, ${number})`;
}

interface ParticleCardProps extends BaseCardProps {
  variant?: 'particle';
  particleCount?: number;
  particleColors?: string[];
}

interface StandardCardProps extends BaseCardProps {
  variant?: 'standard';
  hover?: boolean;
}

type CardProps = PixelCardWrapperProps | SpotlightCardWrapperProps | ParticleCardProps | StandardCardProps;

const Card: React.FC<CardProps> = (props) => {
  const { colors } = useTheme();
  const { children, className = '', padding = 'md' } = props;

  const paddingClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  // Standard Card (default)
  if (!props.variant || props.variant === 'standard') {
    const standardProps = props as StandardCardProps;
    return (
      <div
        className={`rounded-lg border transition-all duration-200 ${paddingClasses[padding]} ${
          standardProps.hover ? 'hover:shadow-sm hover:border-opacity-60' : ''
        } ${className}`}
        style={{
          backgroundColor: colors.background,
          borderColor: colors.borderLight,
        }}
      >
        {children}
      </div>
    );
  }

  // Pixel Card - Use sparingly for special emphasis
  if (props.variant === 'pixel') {
    const pixelProps = props as PixelCardWrapperProps;
    return (
      <PixelCard
        variant="default"
        gap={8}
        speed={1}
        colors={colors.accent}
        noFocus={!pixelProps.interactive}
        className={`rounded-lg ${paddingClasses[padding]} ${className}`}
      >
        {children}
      </PixelCard>
    );
  }

  // Spotlight Card - Use for important sections
  if (props.variant === 'spotlight') {
    const spotlightProps = props as SpotlightCardWrapperProps;
    return (
      <div className={`relative ${className}`}>
        <SpotlightCard
          className={`rounded-lg border ${paddingClasses[padding]}`}
          spotlightColor={spotlightProps.spotlightColor || (`rgba(59, 130, 246, 0.08)` as `rgba(${number}, ${number}, ${number}, ${number})`)}
        >
          {children}
        </SpotlightCard>
      </div>
    );
  }

  // Particle Card - Use very sparingly for special effects
  if (props.variant === 'particle') {
    const particleProps = props as ParticleCardProps;
    return (
      <div
        className={`relative rounded-lg border overflow-hidden ${paddingClasses[padding]} ${className}`}
        style={{
          backgroundColor: colors.background,
          borderColor: colors.borderLight,
        }}
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <Particles
            particleCount={particleProps.particleCount || 30}
            particleSpread={6}
            speed={0.3}
            particleColors={particleProps.particleColors || [colors.accent]}
            alphaParticles={true}
            particleBaseSize={20}
            sizeRandomness={0.4}
            cameraDistance={12}
            disableRotation={true}
            className="w-full h-full"
          />
        </div>
        <div className="relative z-10">
          {children}
        </div>
      </div>
    );
  }

  return null;
};

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  description?: string;
  variant?: 'standard' | 'pixel' | 'spotlight';
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  unit,
  description,
  variant = 'standard',
  trend,
  trendValue,
}) => {
  const { colors } = useTheme();

  const getTrendIcon = () => {
    if (trend === 'up') {
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
        </svg>
      );
    }
    if (trend === 'down') {
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-9.2 9.2M7 7v10h10" />
        </svg>
      );
    }
    return null;
  };

  const getTrendColor = () => {
    if (trend === 'up') return colors.success;
    if (trend === 'down') return colors.danger;
    return colors.textMuted;
  };

  return (
    <Card variant={variant} padding="md" hover={variant === 'standard'}>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.textMuted }}>
            {title}
          </h3>
          {trend && trendValue && (
            <div className="flex items-center space-x-1" style={{ color: getTrendColor() }}>
              {getTrendIcon()}
              <span className="text-xs font-medium">{trendValue}</span>
            </div>
          )}
        </div>
        <div className="flex items-baseline space-x-2">
          <span className="text-xl font-semibold" style={{ color: colors.text }}>
            {value}
          </span>
          {unit && (
            <span className="text-sm" style={{ color: colors.textMuted }}>
              {unit}
            </span>
          )}
        </div>
        {description && (
          <p className="text-xs" style={{ color: colors.textSecondary }}>
            {description}
          </p>
        )}
      </div>
    </Card>
  );
};

// Section Card Component
interface SectionCardProps {
  title: string;
  children: React.ReactNode;
  variant?: 'standard' | 'spotlight' | 'particle';
  headerActions?: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  children,
  variant = 'standard',
  headerActions,
  collapsible = false,
  defaultExpanded = true,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  return (
    <Card variant={variant} padding="lg" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold" style={{ color: colors.text }}>
            {title}
          </h2>
          {collapsible && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 rounded transition-colors duration-200 hover:bg-opacity-10"
              style={{ color: colors.textMuted }}
              aria-label={isExpanded ? t('common.collapseContent') : t('common.expandContent')}
            >
              <svg 
                className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </div>
        {headerActions && (
          <div className="flex items-center space-x-2">
            {headerActions}
          </div>
        )}
      </div>

      {/* Content */}
      {(!collapsible || isExpanded) && (
        <div className="transition-all duration-300">
          {children}
        </div>
      )}
    </Card>
  );
};

export default Card; 